import { ChangeDetectorRef, Component, OnInit, OnDestroy, ChangeDetectionStrategy, inject, signal, viewChild } from '@angular/core';
import { Location, AsyncPipe } from '@angular/common';
import {ActivatedRoute, Router} from '@angular/router';
import {TemplateGeneratorService} from '@app/services/template-generator.service';
import { FormBuilder, FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import {LANDING_PAGE_TEMPLATE_TYPE_ID} from '@app/consts';
import * as routes from '@app/routes';
import {Observable} from 'rxjs';
import {LoaderService} from '@app/services/loader.service';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {TemplateEditorComponent} from '@app/_components/template-editor/template-editor.component';
import {select, Store} from '@ngrx/store';
import {
  CampaignUpdate, getInfluencerError, getInfluencersTypes, getLoggedInUser, getTemplateError,
  getTemplates,
  getTemplateSectionError,
  getTemplateSections,
  getTemplateSectionTypes,
  getTemplateTypes, IAuthenticationState,
  ICampaignState, IInfluencersState, InfluencersTypeList,
  ITemplateSectionState,
  ITemplateState, ResetInfluencerState, ResetTemplateSectionState,
  ResetTemplateState,
  TemplateGetTypes,
  TemplateListAll,
  TemplateSectionListAll,
  TemplateSectionTypeList
} from '@app/stores';
import {ToastrService} from 'ngx-toastr';
import {SocketService} from '@app/services/socket.service';
import * as _ from 'lodash';
import {NgbDropdown, NgbDropdownMenu, NgbDropdownToggle, NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {TemplateRevisionsComponent} from '@app/_components/template-revisions/template-revisions.component';
import Swal from 'sweetalert2';
import { InfluencerType, User } from '@app/modules/shared/models';
import {CanComponentDeactivate} from '@app/_guards/page-changes.guard';
import {NewEditorComponent} from '../new-editor';
import {templateHtml} from '../new-editor/template-html';
import {hasVariableToken} from '@app/_helpers/variable-tokens';
import {startPanelResize} from '@app/_helpers/panel-resize';
import {NewEditorPreviewComponent, PreviewNavigationState} from '../new-editor/preview';
import { NewEditorComponent as NewEditorComponent_1 } from '../new-editor/index';
import { PageShellComponent } from '../../../_components/page-shell/page-shell.component';
import { StatusBarComponent } from '../../status-bar/status-bar.component';
import { PreviewInfluencerNavComponent } from '../preview-influencer-nav/preview-influencer-nav.component';
import { TemplateEditorComponent as TemplateEditorComponent_1 } from '../../../_components/template-editor/template-editor.component';
import { DeviceToolbarComponent } from '../../../_components/device-toolbar/device-toolbar.component';
import {SbModalService} from '@app/_components/unified/sb-modal/sb-modal.service';

type LandingTemplateForm = FormGroup<{
  name: FormControl<string | null>;
  influencer_type_id: FormControl<number | string | null>;
  visibility: FormControl<boolean | null>;
}>;

type SaveTemplateForm = FormGroup<{
  saveAsTemplateName: FormControl<string | null>;
  influencer_type_id: FormControl<number | string | null>;
}>;

const STYLE_KEYS = ['max_width', 'margin_top', 'background_color', 'block_margin', 'block_padding', 'block_background',
  'font_color', 'header_font_color', 'link_color', 'button_color', 'button_font_color', 'button_border_color',
  'button_hover_color', 'font_family', 'header1_font_family', 'header2_font_family', 'header3_font_family',
  'header4_font_family', 'header5_font_family', 'header6_font_family', 'custom_css', 'custom_html_head_code',
  'custom_html_body_code'] as const;

type LandingStylesForm = FormGroup<Record<typeof STYLE_KEYS[number], FormControl<string | null>>>;

@Component({
  selector: 'app-landing',
  templateUrl: './index.html',
  styleUrls: ['./index.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NewEditorComponent_1, PageShellComponent, StatusBarComponent, PreviewInfluencerNavComponent, NewEditorPreviewComponent, TemplateEditorComponent_1, DeviceToolbarComponent, ReactiveFormsModule, AsyncPipe, NgbDropdown, NgbDropdownToggle, NgbDropdownMenu]
})
export class LandingPageComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  private activatedRoute = inject(ActivatedRoute);
  private location = inject(Location);
  private router = inject(Router);
  private loaderService = inject(LoaderService);
  private templateGeneratorService = inject(TemplateGeneratorService);
  private templateSectionStore = inject<Store<ITemplateSectionState>>(Store);
  private templateStore = inject<Store<ITemplateState>>(Store);
  private campaignStore = inject<Store<ICampaignState>>(Store);
  private formBuilder = inject(FormBuilder);
  private toastr = inject(ToastrService);
  private socketService = inject(SocketService);
  private _modalService = inject(NgbModal);
  private readonly sbModal = inject(SbModalService);
  private authenticationStore = inject<Store<IAuthenticationState>>(Store);
  private influencersStore = inject<Store<IInfluencersState>>(Store);
  // The page's fields, the template and its editor data, are written from the store, the
  // template generator service and dialogs' promises: each of those marks the view.
  private changeDetector = inject(ChangeDetectorRef);

  readonly templateEditor = viewChild<TemplateEditorComponent>('templateEditor');
  readonly newEditorRef = viewChild<NewEditorComponent>('newEditorRef');
  readonly landingPreview = viewChild<NewEditorPreviewComponent>('landingPreview');
  public previewNavigation: PreviewNavigationState = {page: 1, total: 0, name: '', loading: true};
  public step;
  public campaign;
  public variables;
  public projectId;
  public viewportSize = 'desktop';
  public template: any;
  public editHtml = false;
  public templates = [];
  public rawTemplatesTypes = [];
  public templateForm: LandingTemplateForm;
  public stylesForm: LandingStylesForm;
  templateSaveType = '';
  public templateTypeId = LANDING_PAGE_TEMPLATE_TYPE_ID;
  subscription: any;
  saveAsTemplateName = '';
  saveAsTemplateNameSubmit = false;
  switchedToMobile = false;
  public selectedSection;
  // Set by the editors' OnTemplateLoaded. The old editor sits in an @if view, which Angular refreshes only
  // after this view's own bindings. When a landing page comes after its 3 s wait for one, the editor emits
  // false from its ngOnChanges there, after the status bar, Back, Change template and Next were checked.
  // A signal has Angular refresh this view again in the same pass; a plain field left them showing the
  // step as loaded (NG0100 in dev builds).
  readonly isTemplateLoaded = signal(false);
  public sections: Array<string> = [];
  private clickEvents = {
    saveClick: false,
    nextClick: false,
    previewClick: false,
    editorSave: false
  };
  private editorSaved = false;
  private templateSubscription;
  loader$: Observable<boolean>;
  unsubscriber = new Subject();
  public defaultProperties = {
    max_width: '',
    margin_top: '0',
    block_margin: '',
    block_padding: '',
    block_background: '#ffffff',
    background_color: '#ffffff',
    font_color: '#000000',
    header_font_color: '#000000',
    link_color: '#000000',
    button_color: '#329ef7',
    button_font_color: '#ffffff',
    button_border_color: '#329ef7',
    button_hover_color: '',
    font_family: 'Arial',
    header_font_family: 'Arial',
    custom_css: '',
    custom_html_head_code: '',
    custom_html_body_code: ''
  };

  landingPageEnabled = true;
  templateReset = false;
  currentUser: User;
  influencerTypes: InfluencerType[] = [];
  newDummyInfluencerType: any = {};
  public saveTemplateForm: SaveTemplateForm;
  influencerTypeId: any;
  hasUnsavedChanges = false;
  isSaving = false;
  private contentChangeEnabled = false;
  public newEditor = {
    show: false,
    active: false
  };
  private blankEditorOpened = false;
  public templateData = {
    body: null,
    bodyJson: null,
    style: null,
    visualEditorId: null,
    campaignId: null,
    projectId: null,
    influencerTypeId: null,
    templateId: null,
    templateTypeId: null,
  };

  defaultPanelWidth = 700;
  panelWidth = this.defaultPanelWidth;
  panelCollapsed = false;

  constructor() {
    // window.onbeforeunload = function(event) {
    //   event.preventDefault();
    //   event.returnValue = true;
    // };
    this.templateStore.dispatch(ResetTemplateState({params: {previewUrl: null, error: '', success: ''}}));
    this.templateStore.dispatch(ResetTemplateSectionState({params: {error: '', templateSectionTypes: [], templateSections: []}}));
    this.templateGeneratorService.subscribeStore();
    this.subscribeStore();
    this.subscription = this.socketService.sourceData
      .subscribe((message: any) => {
        if (message?.data?.requesterId) {
          const temp = _.cloneDeep(this.templates);
          temp.map(element => {
            if (element.id === message.data.requesterId && message.data?.requesterType === 'section') {
              element.thumbnail = message.data.result.thumbnail;
            }
            return element;
          });
          this.templates = [...temp];
          this.changeDetector.markForCheck();
        }
      },
      (err) => console.error(err),
      () => console.warn('Completed!')
      );
  }

  canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
    if (this.isSaving) {
      return true;
    }
    return !this.hasUnsavedChanges;
  }

  beforeUnloadHandler = (event: BeforeUnloadEvent) => {
    if (this.hasUnsavedChanges) {
      event.preventDefault();
      event.returnValue = ''; // Show a warning dialog in most browsers
    }
  };

  subscribeStore() {
    this.authenticationStore.pipe(select(getLoggedInUser))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(user => {
        if (user) {
          this.currentUser = user;
          this.changeDetector.markForCheck();
        }
      }
      );
    this.templateSectionSubscribeStore();
    this.influencerStoreSubscribe();
  }

  influencerStoreSubscribe() {
    this.influencersStore.pipe(select(getInfluencerError))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(error => {
        if (error) {
          this.toastr.error(error, 'Error');
        }
      });

    this.influencersStore.pipe(select(getInfluencersTypes))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(influencerTypes => {
        if (influencerTypes && influencerTypes.length > 0) {
          this.newDummyInfluencerType.id = null;
          this.newDummyInfluencerType.name = 'All';
          this.influencerTypes = [
            this.newDummyInfluencerType,
            ...influencerTypes
          ];
          this.changeDetector.markForCheck();
        }
      });
  }

  templateSectionSubscribeStore() {
    this.templateSectionStore.dispatch(TemplateSectionTypeList({params: {}}));
    this.templateSectionStore.pipe(select(getTemplateSections))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(templateSections => {
        if (templateSections && templateSections.length > 0) {
          this.templates = templateSections;
          this.changeDetector.markForCheck();
        }
      });
    this.templateSectionStore.pipe(select(getTemplateSectionTypes))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(templateSectionTypes => {
        if (templateSectionTypes && templateSectionTypes.length > 0) {
          this.rawTemplatesTypes = templateSectionTypes.filter(x => x.name !== 'social images');
          this.fetchRawTemplatesById(this.rawTemplatesTypes[0].id);
          this.changeDetector.markForCheck();
        }
      });
    this.templateSectionStore.pipe(select(getTemplateSectionError))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(error => {
        if (error) {
          this.toastr.error(error, 'Error');
        }
      });
  }

  fetchRawTemplatesById(id) {
    // const options = this.currentUser?.can('view-advanced-editor', 'template') ? {includeOnlyVisible: true} : {};
    this.templateSectionStore.dispatch(TemplateSectionListAll({
      params: {
        // options: JSON.stringify(options),
        template_section_type_id: id,
        template_type_id: LANDING_PAGE_TEMPLATE_TYPE_ID
      }
    }));
  }

  ngOnInit() {
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
    this.loader$ = this.loaderService.loader$;
    this.templateGeneratorService.clearTemplate(LANDING_PAGE_TEMPLATE_TYPE_ID);
    this.saveTemplateForm = this.formBuilder.group({
      saveAsTemplateName: new FormControl<string | null>('', [Validators.required]),
      influencer_type_id: new FormControl<number | string | null>(null),
    });
    this.initializeForms();
    this.activatedRoute.params.subscribe(() => {
      if (this.campaign) {
        this.templateGeneratorService.loadClientTemplate({templateTypeId: this.templateTypeId});
      }
      this.subscribeToTemplateChanges();
      if (this.campaign) {
        this.setStep();
      }
      this.changeDetector.markForCheck();
    }
    );
    this.influencersStore.dispatch(InfluencersTypeList());
    this.activatedRoute.queryParams.subscribe(async (params) => {
      const {sourceTemplateId} = params;
      this.subscribeToCampaign(sourceTemplateId || 0);
    });
  }

  subscribeToTemplateChanges() {
    if (this.templateSubscription) {
      this.templateSubscription.unsubscribe();
    }
    this.templateSubscription = this.templateGeneratorService.onTemplateChanged[this.templateTypeId]
      .pipe(takeUntil(this.unsubscriber))
      .subscribe((template) => {
        if (template) {
          if (!this.clickEvents.nextClick) {
            this.replaceTemplate(template);
            //console.log('this.template', this.template);
            this.templateData = {
              visualEditorId: this.template.visual_editor_id,
              body: this.template.body,
              bodyJson: this.template.body_json,
              style: this.template.style,
              campaignId: this.template.campaign_id,
              projectId: this.campaign?.project_id || null,
              influencerTypeId: this.template.influencer_type_id,
              templateId: this.template.id,
              templateTypeId: this.template.template_type_id
            };
            this.changeDetector.markForCheck();
          }
        }
      });
    this.templateGeneratorService.onCampaignUpdated
      .pipe(takeUntil(this.unsubscriber))
      .subscribe((template) => {
        if (template) {
          this.hasUnsavedChanges = false;
          this.isSaving = false;
          if (this.clickEvents.saveClick) {
            this.router.navigateByUrl('/' + routes.campaigns({projectId: this.campaign.project_id}));
          }
          if (this.clickEvents.nextClick) {
            this.router.navigateByUrl(
              routes.campaignShare({
                projectId: this.campaign.project_id,
                campaignId: this.campaign.id
              })
            );
          }

          if (this.clickEvents.previewClick) {
            this.templateGeneratorService.preview(this.template.id);
          }
          // onCampaignUpdated replays the last saved template, which may be another campaign's.
          if (this.clickEvents.editorSave) {
            this.editorSaved = true;
            this.toastr.success('Landing page saved', 'Success');
          }
          this.clickEvents = {
            saveClick: false,
            nextClick: false,
            previewClick: false,
            editorSave: false
          };
          this.changeDetector.markForCheck();
        }
      });

    this.hasUnsavedChanges = false;
  }

  onPageSizeSelect(event) {
    this.viewportSize = event;
    if (event === 'mobile') {
      this.switchedToMobile = true;
    }
  }

  onEditHtml() {
    this.templateEditor().onEditHtml();
  }

  subscribeToCampaign(sourceTemplateId: number) {
    this.templateGeneratorService.onCampaignLoaded
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(camp => {
        if (camp) {
          this.campaign = camp;
          this.landingPageEnabled = this.campaign.landing_page_status === 'active';
          const segment = this.campaign.segments?.[0];
          // A campaign without a segment has no influencers to build a landing page for: they come first.
          if (!segment) {
            this.router.navigate(
              ['/project/' + this.campaign.project_id + '/add-influencer'],
              {queryParams: {campId: this.campaign.id}}
            );
            return;
          }
          if (this.activatedRoute.snapshot.queryParams?.blank) {
            this.templateData = {
              ...this.templateData,
              campaignId: this.campaign.id,
              projectId: this.campaign.project_id,
              influencerTypeId: segment.influencer_type_id,
              templateTypeId: LANDING_PAGE_TEMPLATE_TYPE_ID
            };
            this.templateForm.patchValue({
              name: this.templateForm.value.name || this.campaign.name || '',
              influencer_type_id: segment.influencer_type_id
            });
            // A blank page starts in the new editor (the type's default editor, as a campaign's
            // first social image does); closing it unsaved leaves the old editor's blank page.
            if (!this.blankEditorOpened && !this.template?.id) {
              this.blankEditorOpened = true;
              this.showNewEditor();
            }
          }
          this.templateGeneratorService.loadVariables({influencerTypeId: segment.influencer_type_id, campaignId: this.campaign.id},
            (vars) => {
              this.variables = vars;
              this.changeDetector.markForCheck();
            });
          //
          // Do not load template automatically when there is blank queryParam
          if (!this.activatedRoute.snapshot.queryParams?.blank) {
            this.templateGeneratorService.loadClientTemplate(
              {
                templateTypeId: LANDING_PAGE_TEMPLATE_TYPE_ID,
                sourceTemplateId: sourceTemplateId !== 0 ? sourceTemplateId : '',
              },
              {createIfNotExist: false, forceTouch: false}
            );
          }

          this.setStep();
          this.changeDetector.markForCheck();
        }
      });
  }

  getVariables() {
    if (this.campaign) {
      return this.variables;
    }
  }

  replaceTemplate(template) {
    if (template) {
      let t: any = {};
      if (this.templateReset) {
        this.templateReset = false;
        this.template = {...this.template};
        this.template.body = template.body;
        this.template.style = template.style;
        this.template.properties = template.properties;
        t = this.template || {};
      } else {
        this.template = template;
        t = template || {};
      }

      if (template?.visual_editor_id) {
        this.newEditor.active = true;
        this.hidePanel();

      } else {
        this.newEditor.active = false;
      }

      this.templateForm.setValue({
        name: t.name || this.campaign?.name || '',
        influencer_type_id: t.influencer_type_id ?? null,
        visibility: t.visibility === undefined ? true : !!t.visibility
      });
      const formFields = {};
      let properties = t.properties;
      if (properties) {
        while (typeof properties === 'string') {
          properties = JSON.parse(properties);
        }
        properties = {...properties};
        Object.entries(this.stylesForm.controls).forEach(([key, val]) => {
          this.setDefaultProperties(key, properties);
          formFields[key] = properties[key];
        });
      }
      this.stylesForm.patchValue(formFields);
      // Also from the revisions dialog's promise.
      this.changeDetector.markForCheck();
    }
  }

  setDefaultProperties(key, properties) {
    if (!properties[key]) {
      properties[key] = this.defaultProperties[key];
    }
  }

  setStep() {
    this.step = {
      title: 'Landing Page',
      params: {
        projectId: this.campaign.project_id,
        campaignId: this.campaign.id,
        campaignName: this.campaign.name
      }
    };
  }

  initializeForms() {
    this.templateForm = this.formBuilder.group({
      name: new FormControl<string | null>('', [Validators.required]),
      influencer_type_id: new FormControl<number | string | null>(null),
      visibility: new FormControl<boolean | null>(true)
    });
    this.stylesForm = this.formBuilder.group({
      max_width: new FormControl<string | null>(''),
      margin_top: new FormControl<string | null>('0'),
      background_color: new FormControl<string | null>(''),
      block_margin: new FormControl<string | null>(''),
      block_padding: new FormControl<string | null>(''),
      block_background: new FormControl<string | null>(''),
      font_color: new FormControl<string | null>(''),
      header_font_color: new FormControl<string | null>(''),
      link_color: new FormControl<string | null>(''),
      button_color: new FormControl<string | null>(''),
      button_font_color: new FormControl<string | null>(''),
      button_border_color: new FormControl<string | null>(''),
      button_hover_color: new FormControl<string | null>(''),
      font_family: new FormControl<string | null>(''),
      header1_font_family: new FormControl<string | null>(''),
      header2_font_family: new FormControl<string | null>(''),
      header3_font_family: new FormControl<string | null>(''),
      header4_font_family: new FormControl<string | null>(''),
      header5_font_family: new FormControl<string | null>(''),
      header6_font_family: new FormControl<string | null>(''),
      custom_css: new FormControl<string | null>(''),
      custom_html_head_code: new FormControl<string | null>(''),
      custom_html_body_code: new FormControl<string | null>('')
    });
  }

  async buildTemplateForSave(htmlParts?: any) {
    let template: any = {};
    if (this.template) {
      template = {...this.template};
    }
    template.properties = {};
    const {name, influencer_type_id, visibility} = this.templateForm.value;
    if (!this.newEditor.active) {
      if (!htmlParts) {
        htmlParts = await this.templateEditor().buildTemplateForSave();
      }
      const {value} = this.stylesForm;
      Object.entries(value).forEach(([key, val]) => {
        template.properties[key] = val;
      });
      let htmlBody: any = htmlParts.body;
      htmlBody = htmlBody.replaceAll(' fp-active', '');
      htmlBody = htmlBody.replaceAll('fp-active', '');
      template.body = htmlBody.replaceAll(/data-id="[^"]*"/g, '');
      template.body_json = null;
      template.style = htmlParts.style;
    } else {
      template.visual_editor_id = this.templateData.visualEditorId;
      template.body = this.templateData.body;
      template.body_json = this.templateData.bodyJson;
      template.style = null;
    }
    template.name = name;
    template.template_type_id = this.templateData.templateTypeId;
    template.influencer_type_id = influencer_type_id;
    template.visibility = visibility;
    template.client_id = this.campaign.client_id;
    template.project_id = this.campaign.project_id;
    template.campaign_id = this.templateData.campaignId;
    delete template.content_hash;
    return template;
  }

  // async buildTemplateForSave(htmlParts?: any) {
  //   if (!htmlParts) {
  //     htmlParts = await this.templateEditor.buildTemplateForSave();
  //   }
  //   const {name} = this.templateForm.value;
  //   const {value} = this.stylesForm;
  //   let template: any = {};
  //   if (this.template) {
  //     template = {...this.template};
  //   }
  //
  //   let htmlBody: any = htmlParts.body;
  //   htmlBody = htmlBody.replaceAll(' fp-active', '');
  //   htmlBody = htmlBody.replaceAll('fp-active', '');
  //   template.body = htmlBody.replaceAll(/data-id="[^"]*"/g, '');
  //   template.style = htmlParts.style;
  //   template.name = name;
  //   template.properties = {};
  //   Object.entries(value).forEach(([key, val]) => {
  //     template.properties[key] = val;
  //   });
  //   delete template.content_hash;
  //   return template;
  // }

  getForm() {
    return {...this.stylesForm.value};
  }

  onContentChanged() {
    if (!this.contentChangeEnabled) {
      return;
    }
    this.hasUnsavedChanges = true;
    this.switchedToMobile = false;
  }

  onStylesCustomizationChange(params) {
    if (this.contentChangeEnabled) {
      this.hasUnsavedChanges = true;
      this.switchedToMobile = false;
    }
    this.stylesForm.patchValue({...params});
  }

  onBackButtonClick(forceSelect = false) {
    if (forceSelect) {
      this.router.navigateByUrl(routes.campaignSelectTemplate({projectId: this.campaign.project_id, campaignId: this.campaign.id},
        {select: true}));
    } else {
      this.router.navigateByUrl(routes.addInfluencers({projectId: this.campaign.project_id}, {campId: this.campaign.id}));
    }
  }

  private async confirmMobileView(): Promise<boolean> {
    if (!this.hasUnsavedChanges || this.switchedToMobile) {
      return true;
    }
    const res = await Swal.fire({
      title: 'Please review the mobile version before saving.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Save Anyway',
      cancelButtonText: 'Cancel',
      width: '35%',
      allowOutsideClick: false,
      allowEscapeKey: false
    });
    return !!res.value;
  }

  private hasValidTemplateDetails() {
    this.templateForm.markAllAsTouched();
    if (this.templateForm.valid) {
      return true;
    }

    if (this.templateForm.get('name')?.hasError('required')) {
      this.toastr.error('Template Name is required', 'Error');
    }
    return false;
  }

  async onSaveButtonClick(saveAndExit = true) {
    if (!this.hasValidTemplateDetails() || !await this.confirmMobileView()) {
      return;
    }
    this.clickEvents = {
      ...this.clickEvents,
      saveClick: saveAndExit
    };
    this.isSaving = true;
    this.saveClientTemplate();
  }

  async saveAsNewTemplate(modal: any) {
    this.saveAsTemplateNameSubmit = true;
    this.saveAsTemplateName = this.saveTemplateForm.get('saveAsTemplateName').value;
    if (this.saveTemplateForm.get('influencer_type_id').value === 'null') {
      this.saveTemplateForm.patchValue({influencer_type_id: null});
    }
    this.influencerTypeId = this.saveTemplateForm.get('influencer_type_id').value;
    if (this.saveAsTemplateName) {
      const template = await this.buildTemplateForSave();
      modal.close();
      let extraParams;
      if (this.templateSaveType === 'client') {
        extraParams = {
          name: this.saveAsTemplateName,
          client_id: this.campaign.client_id,
          project_id: this.campaign.project_id,
          campaign_id: null,
          influencer_type_id: this.influencerTypeId,
        };
      } else {
        extraParams = {
          name: this.saveAsTemplateName,
          client_id: null,
          project_id: null,
          campaign_id: null,
          influencer_type_id: this.influencerTypeId
        };
      }

      this.templateGeneratorService.touchTemplate({
        templateTypeId: LANDING_PAGE_TEMPLATE_TYPE_ID,
        sourceTemplateId: template.id,
        extraParams
      });
    }
  }

  async saveAsNewTemplateModel(template, type) {
    this.saveTemplateForm.patchValue({influencer_type_id: this.campaign?.segments?.[0]?.influencer_type_id ?? null});
    this.templateSaveType = type;
    const modelRef = this._modalService.open(template, {
      centered: false,
      size: 'sm',
      keyboard: true
    });
    modelRef.result.then((result) => {
    }, (reason) => {
    });
  }

  async onNextButtonClick() {
    if (!this.hasValidTemplateDetails() || !await this.confirmMobileView()) {
      return;
    }
    // The editor's Save already saved the template and the landing page status.
    if (this.newEditor.active && this.editorSaved && !this.hasUnsavedChanges) {
      this.router.navigateByUrl(routes.campaignShare({projectId: this.campaign.project_id, campaignId: this.campaign.id}));
      return;
    }
    let template;
    if (!this.newEditor.active) {
      // Validate template, body should not be empty
      template = await this.templateEditor().buildTemplateForSave();
      if (!template?.body) {
        return this.toastr.error('Template is empty, add some content and try again', 'Error');
      }
    }

    this.clickEvents = {
      ...this.clickEvents,
      nextClick: true
    };
    this.isSaving = true;
    if (this.newEditor.active) {
      this.saveClientTemplate(this.template);
    } else {
      this.saveClientTemplate(template);
    }
  }

  async saveClientTemplate(template?: any) {
    const clientTemplate = await this.buildTemplateForSave(template);
    const html = templateHtml(clientTemplate.body, clientTemplate.body_json);
    // if (!clientTemplate.body.includes('#InfluencerRegistrationUrl#')) {
    if (!['#InfluencerCallToActionUrl#', '#InfluencerCTA#', '#ProjectCallToActionUrl#', '#ProjectCTA#']
      .some((key) => hasVariableToken(html, key))) {
      Swal.fire({
        // title: '#InfluencerRegistrationUrl# is not used on the page. This would cause problem with report as well as referral tracking system. Are you sure you want to continue?',
        title: '#InfluencerCallToActionUrl# or #InfluencerCTA# or #ProjectCallToActionUrl# or #ProjectCTA# is not used on the page. This would cause problem with report as well as referral tracking system. Are you sure you want to continue?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Continue',
        cancelButtonText: 'Close',
        width: '70%'
      }).then(result => {
        if (result.value) {
          this.saveTemplateFinal(clientTemplate);
        } else {
          this.isSaving = false;
          this.clickEvents = {
            saveClick: false,
            nextClick: false,
            previewClick: false,
            editorSave: false
          };
        }
      });
    } else {
      this.saveTemplateFinal(clientTemplate);
    }
  }

  saveTemplateFinal(clientTemplate) {
    this.templateGeneratorService.saveClientTemplate({
      id: this.template ? this.template.id : null,
      templateTypeId: this.templateTypeId,
      campaignId: this.campaign.id,
      clientTemplate,
      visualEditorId: null,
    });
    // const campaign = {
    //   landing_page_status: this.landingPageEnabled ? 'active' : 'skipped'
    // };
    const campaign = {
      landing_page_status: 'active'
    };

    if (this.activatedRoute.snapshot.queryParams?.blank) {
      const params = { ...this.activatedRoute.snapshot.queryParams };
      delete params.blank;
      this.location.replaceState(routes.campaignTemplates({
        projectId: this.projectId,
        campaignId: this.campaign.id,
      }, params));
    }

    this.campaignStore.dispatch(CampaignUpdate({campaign, campaignId: this.campaign.id}));
  }

  async onPreviewButtonClick() {
    if (!this.hasValidTemplateDetails() || !await this.confirmMobileView()) {
      return;
    }
    this.clickEvents = {
      ...this.clickEvents,
      previewClick: true
    };
    this.isSaving = true;
    this.saveClientTemplate();
  }

  onSendTestEmailButtonClick() {
    const email = prompt('Please enter email to send the test email to.', 'email');
    if (email) {
      this.templateGeneratorService.sendTestEmail(email);
    }
  }

  templateLoaded(event) {
    this.isTemplateLoaded.set(event);
    if (!this.newEditor.active) {
      this.templateEditor().enableEditor();
    }
    if (event) {
      this.contentChangeEnabled = true;
      this.hasUnsavedChanges = false;
      this.switchedToMobile = false;
    }
  }

  showTemplateRevisions() {
    const modelRef = this.sbModal.openComponent(TemplateRevisionsComponent, {centered: true, keyboard: true, size: 'xl'}, {
      templateId: this.template?.id,
    });
    modelRef.result.then((result) => {
      if (result.reset) {
        this.templateEditor().disableEditor();
        this.templateReset = true;
        this.templateGeneratorService.handlePublicTemplateSelected({templateId: this.template.source_template_id});
      } else {
        this.templateEditor().disableEditor();
        this.replaceTemplate(result.data);
      }
    }, (reason) => {
    });
  }

  changingInfluencerType(e) {
    this.saveTemplateForm.patchValue({influencer_type_id: e});
  }

  showNewEditor() {
    this.newEditor.show = true;
  }

  onPreviewPage(page: number) {
    this.landingPreview()?.goToPage(page);
  }

  onEditorStateChanged(event: { action: string; body: any; bodyJson: string; visualEditorId: any }) {
    if (event.action === 'save') {
      this.templateData = {
        ...this.templateData,
        body: event.body,
        bodyJson: event.bodyJson ? JSON.parse(event.bodyJson) : null,
        visualEditorId: event.visualEditorId
      };
      this.newEditor.active = true;
      this.hidePanel();
      this.newEditor.show = false;
      this.hasUnsavedChanges = true;
      this.editorSaved = false;
      this.saveFromEditor();
      return;
    }
    if (event.action === 'undo-all') {
      this.showNewEditorTemplateRevisions();
      return;
    }
    this.newEditor.show = false;
  }

  // The editor closes on Save, so its document is saved the way Next saves it.
  private saveFromEditor() {
    if (!this.hasValidTemplateDetails()) {
      return;
    }
    this.clickEvents = {...this.clickEvents, editorSave: true};
    this.isSaving = true;
    this.saveClientTemplate();
  }

  showNewEditorTemplateRevisions() {
    const modelRef = this.sbModal.openComponent(TemplateRevisionsComponent, {
      centered: true, keyboard: true, size: 'xl',
      windowClass: 'above-editor-modal',
      backdropClass: 'above-editor-modal-backdrop'
    }, {
      templateId: this.template?.id,
      canReset: false,
    });
    modelRef.result.then((result) => {
      const newEditorRef = this.newEditorRef();
      if (result.data && newEditorRef) {
        newEditorRef.loadTemplate(result.data);
      }
    }, () => {});
  }

  startResizing(event: PointerEvent) {
    startPanelResize(event, this.panelWidth, width => {
      this.panelWidth = width;
      this.changeDetector.markForCheck();
    });
  }

  togglePanel() {
    this.panelCollapsed = !this.panelCollapsed;
    if (this.panelCollapsed) {
      this.panelWidth = 25;
    } else {
      this.panelWidth = this.defaultPanelWidth;
    }
  }

  hidePanel() {
    this.panelCollapsed = true;
  }

  ngOnDestroy() {
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    this.templateEditor()?.destroy();
    this.templateGeneratorService.unsubscribe();
    this.unsubscriber.next(undefined);
    this.unsubscriber.complete();
    this.subscription.unsubscribe();
  }
}
